import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Watermark from "@/components/Watermark";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import SectionHead from "@/components/SectionHead";
import { PRACTICE_AREAS, PRINCIPLES, ASSESSMENT_STEPS, NAVIGATOR_OPTIONS, PANEL_PILLARS, IMAGES } from "@/data/content";
import { API } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative flex min-h-screen items-center overflow-hidden bg-navy pt-24">
      <Watermark text="SMART LEGAL SOLUTIONS" mobileText="SLS" />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-14 px-5 pb-24 pt-10 lg:grid-cols-12 lg:px-10">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mono-label"
            data-testid="hero-kicker"
          >
            Advocacy &middot; Strategy &middot; Legal Research
          </motion.p>

          <div className="mt-8">
            <MaskedLine delay={0.35}>
              <span className="font-serif text-[17vw] font-medium leading-[0.9] tracking-tight text-ivory sm:text-8xl lg:text-[7.5rem]">
                SMART
              </span>
            </MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="mt-3 block font-mono text-[11px] uppercase tracking-[0.65em] text-gold sm:text-sm">
                Legal Solutions
              </span>
            </MaskedLine>
          </div>

          <h1 className="mt-12 font-serif text-4xl font-medium leading-[1.12] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.7}>Clarity Before Action.</MaskedLine>
            <MaskedLine delay={0.85}>
              <span className="italic text-gold/90">Strategy Before Litigation.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ivory/60"
          >
            Professional legal information and structured case assessment across selected civil, criminal,
            constitutional, property, family and commercial matters &mdash; Vijayawada, Andhra Pradesh.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/practice-areas" data-testid="hero-practice-areas-btn" className="btn-outline-gold">
              View Areas of Practice <ArrowRight size={13} />
            </Link>
            <Link to="/contact" data-testid="hero-contact-office-btn" className="btn-ghost-ivory">
              Contact Office
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-12 max-w-lg font-mono text-[10px] leading-relaxed tracking-[0.08em] text-slate/80"
            data-testid="hero-disclaimer"
          >
            Website content is provided for general informational purposes and does not constitute legal advice or
            solicitation.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:col-span-5 lg:block"
        >
          <div className="absolute -left-4 -top-4 h-full w-full border border-gold/40" aria-hidden="true" />
          <div className="relative overflow-hidden">
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.hero}
              alt="A quiet, professionally arranged legal office interior with timber desk and natural light"
              className="h-[34rem] w-full scale-110 object-cover grayscale-[35%] contrast-[1.05]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 border-l-2 border-gold/70 pl-4">
              <p className="font-serif text-lg italic text-ivory">A practice built on preparation.</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/50">Vijayawada &middot; Andhra Pradesh</p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 lg:block"
        aria-hidden="true"
      >
        <div className="h-14 w-px bg-gradient-to-b from-gold/70 to-transparent" />
      </motion.div>
    </section>
  );
};

const Navigator = () => {
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();
  const active = NAVIGATOR_OPTIONS.find((o) => o.key === selected);

  return (
    <section data-testid="matter-navigator-section" className="relative bg-warm py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <SectionHead dark label="Smart Practice-Area Navigator" title="Identify the Relevant Practice Area" />
        <Reveal delay={0.1} className="mt-10">
          <div className="flex flex-wrap gap-2.5" role="group" aria-label="Select the general nature of your matter">
            {NAVIGATOR_OPTIONS.map((o) => (
              <button
                key={o.key}
                data-testid={`navigator-option-${o.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setSelected(o.key === selected ? null : o.key)}
                className={`border px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                  selected === o.key ? "border-navy bg-navy text-ivory" : "border-navy/25 text-navy/70 hover:border-gold hover:text-navy"
                }`}
              >
                {o.key}
              </button>
            ))}
          </div>
        </Reveal>
        {active && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 border border-navy/15 bg-ivory p-8 sm:p-10"
            data-testid="navigator-result-panel"
          >
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <p className="mono-label-dark">Potentially Relevant Area</p>
                <p className="mt-3 font-serif text-2xl font-medium text-navy sm:text-3xl">{active.area}</p>
                <button
                  data-testid="navigator-submit-matter-btn"
                  onClick={() => navigate("/contact", { state: { area: active.area } })}
                  className="btn-outline-dark mt-7"
                >
                  Submit Matter Details <ArrowRight size={13} />
                </button>
              </div>
              <div>
                <p className="mono-label-dark">Documents That May Be Useful</p>
                <ul className="mt-4 space-y-2.5">
                  {active.docs.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-sm text-navy/70">
                      <span className="mt-2 h-px w-4 shrink-0 bg-gold" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-8 border-t border-navy/10 pt-5 font-mono text-[10px] leading-relaxed tracking-[0.06em] text-slate">
              This tool provides general navigation only. It does not provide legal advice, determine the merits of a
              matter or establish an advocate-client relationship.
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default function Home() {
  useSEO(
    "Smart Legal Solutions | Advocate & Legal Information | Vijayawada",
    "Professional information concerning selected litigation, advisory, legal research and documentation matters in Vijayawada, Andhra Pradesh."
  );
  const [insights, setInsights] = useState([]);
  const [advocates, setAdvocates] = useState([]);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    API.get("/insights").then((r) => setInsights(r.data.slice(0, 3))).catch(() => {});
    API.get("/advocates").then((r) => setAdvocates(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={PRACTICE_AREAS.map((a) => a.title)} className="bg-ink" />

      {/* Professional Approach */}
      <section data-testid="approach-section" className="bg-warm py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHead dark label="The Smart Legal Approach" title="Every Legal Problem Begins with the Right Question." />
              <Reveal delay={0.15} className="mt-8">
                <p className="max-w-md text-base leading-relaxed text-navy/60">
                  A legal issue cannot be assessed from one document, one allegation or one version of events. A
                  structured review ordinarily requires the relevant facts, applicable law, procedural stage, available
                  documents, limitation, jurisdiction and the relief legally open in the circumstances.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              {PRINCIPLES.map((p, i) => (
                <Reveal key={p.n} delay={i * 0.08}>
                  <div data-testid={`principle-${p.n}`} className="group flex items-baseline gap-6 border-b border-navy/10 py-7 transition-colors duration-300 hover:border-gold/60 sm:gap-10">
                    <span className="font-mono text-xs tracking-[0.2em] text-gold">{p.n}</span>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-10">
                      <h3 className="w-48 shrink-0 font-mono text-sm font-medium uppercase tracking-[0.25em] text-navy">{p.title}</h3>
                      <p className="text-sm leading-relaxed text-navy/55">{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Preview */}
      <section data-testid="practice-preview-section" className="relative overflow-hidden bg-navy py-20 sm:py-28">
        <Watermark text="PRACTICE" mobileText="SLS" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
          <SectionHead label="Selected Areas of Practice" title="Structured Legal Analysis Across Distinct Proceedings." />
          <div className="mt-14 grid gap-px bg-ivory/10 sm:grid-cols-2 lg:grid-cols-3">
            {PRACTICE_AREAS.map((a, i) => (
              <Reveal key={a.n} delay={(i % 3) * 0.08} className="bg-navy">
                <Link
                  to="/practice-areas"
                  data-testid={`practice-card-${a.n}`}
                  className="group flex h-full flex-col justify-between p-8 transition-colors duration-500 hover:bg-oxford"
                >
                  <div>
                    <span className="font-mono text-xs tracking-[0.2em] text-gold/70">{a.n}</span>
                    <h3 className="mt-4 font-serif text-2xl font-medium leading-snug text-ivory">{a.title}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-ivory/50">{a.desc}</p>
                  </div>
                  <span className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold/0 transition-all duration-500 group-hover:text-gold">
                    View Information <ArrowUpRight size={12} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/practice-areas" data-testid="practice-preview-view-all-btn" className="btn-outline-gold">
              View Practice Information <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Case Assessment Strip */}
      <section data-testid="assessment-section" className="bg-ivory py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <SectionHead dark label="Before a Legal Step Is Considered" title="What Must Be Examined First?" />
          <div className="mt-14 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-3">
            {ASSESSMENT_STEPS.map((s, i) => (
              <button
                key={s.n}
                data-testid={`assessment-step-${s.n}`}
                onClick={() => setActiveStep(i)}
                className={`group relative bg-ivory p-8 text-left transition-colors duration-500 ${activeStep === i ? "bg-navy" : "hover:bg-warm"}`}
              >
                <span className={`font-serif text-5xl font-medium transition-colors duration-500 ${activeStep === i ? "text-gold" : "text-navy/15 group-hover:text-gold/50"}`}>
                  {s.n}
                </span>
                <h3 className={`mt-5 font-mono text-xs font-medium uppercase tracking-[0.25em] ${activeStep === i ? "text-ivory" : "text-navy"}`}>
                  {s.title}
                </h3>
                <p className={`mt-3 text-sm leading-relaxed ${activeStep === i ? "text-ivory/60" : "text-navy/55"}`}>{s.desc}</p>
                <span className={`absolute bottom-0 left-0 h-0.5 bg-gold transition-all duration-500 ${activeStep === i ? "w-full" : "w-0 group-hover:w-1/3"}`} />
              </button>
            ))}
          </div>
          <Reveal className="mt-10">
            <p className="text-center font-mono text-[11px] tracking-[0.08em] text-slate">
              Legal remedies depend on the facts, documents, applicable law and procedural stage of each matter.
            </p>
          </Reveal>
        </div>
      </section>

      <Navigator />

      {/* Advocate Panel Preview */}
      <section data-testid="panel-preview-section" className="relative overflow-hidden bg-navy py-20 sm:py-28">
        <span aria-hidden="true" className="watermark-text pointer-events-none absolute -right-10 top-1/2 hidden -translate-y-1/2 select-none text-[26rem] lg:block">
          SLS
        </span>
        <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
          <SectionHead label="Our Advocate Panel" title="Collective Legal Insight. Structured Representation." />
          <Reveal delay={0.1} className="mt-8 max-w-2xl">
            <p className="text-base leading-relaxed text-ivory/60">
              Smart Legal Solutions operates through a multidisciplinary panel of advocates supporting diverse legal
              requirements across litigation, advisory, documentation and dispute-resolution matters.
            </p>
            <p className="mt-5 border-l-2 border-gold/60 pl-5 font-serif text-xl italic text-ivory/80">
              A Multidisciplinary Panel of Advocates
            </p>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate">
              A structured network aligned across practice areas, jurisdictions and matter requirements.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-px bg-ivory/10 sm:grid-cols-2 lg:grid-cols-3">
            {advocates.map((a, i) => (
              <Reveal key={a.id || i} delay={(i % 3) * 0.08} className="bg-navy">
                <Link to="/advocate-panel" data-testid={`panel-preview-card-${i}`} className="group flex h-full items-center gap-5 p-6 transition-colors duration-500 hover:bg-oxford">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center border border-gold/40">
                    <span className="font-serif text-base tracking-[0.1em] text-gold">
                      {a.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("")}
                    </span>
                  </div>
                  <div>
                    <p className="font-serif text-lg font-medium text-ivory">{a.name}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/40">
                      {(a.practice_areas || []).slice(0, 2).join(" · ")}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/advocate-panel" data-testid="panel-preview-explore-btn" className="btn-outline-gold">
              Explore Advocate Panel <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Pillars strip */}
      <section className="border-y border-gold/10 bg-ink py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:grid-cols-2 lg:grid-cols-5 lg:px-10">
          {PANEL_PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.07}>
              <div data-testid={`pillar-${i}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ivory">{p.title}</h3>
                <p className="mt-3 text-[13px] leading-relaxed text-ivory/45">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Insights Preview */}
      <section data-testid="insights-preview-section" className="bg-warm py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <SectionHead dark label="SLS Legal Intelligence" title="Understand the Procedure Before Taking the Next Step." />
          <div className="mt-14 grid gap-10 lg:grid-cols-3">
            {insights.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1}>
                <Link to={`/legal-insights/${a.slug}`} data-testid={`insight-preview-${i}`} className="group block border-t-2 border-navy/15 pt-7 transition-colors duration-300 hover:border-gold">
                  <p className="mono-label-dark">{a.category}</p>
                  <h3 className="mt-4 font-serif text-2xl font-medium leading-snug text-navy transition-colors duration-300 group-hover:text-gold">
                    {a.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-navy/55">{a.excerpt}</p>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate">
                    {a.reading_time} min read &middot; Reviewed {a.date_reviewed}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/legal-insights" data-testid="insights-preview-view-all-btn" className="btn-outline-dark">
              Read Legal Insights <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Profile Preview */}
      <section data-testid="profile-preview-section" className="bg-ivory py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-10">
          <div>
            <SectionHead dark label="Professional Profile" title="Professional Responsibility Begins with Careful Preparation." />
            <Reveal delay={0.1} className="mt-8">
              <p className="max-w-lg text-base leading-relaxed text-navy/60">
                The practice is founded on careful document review, procedural clarity, legal research and responsible
                representation. Professional particulars are published only where verifiable.
              </p>
              <Link to="/profile" data-testid="profile-preview-btn" className="btn-outline-dark mt-9">
                View Advocate Profile <ArrowRight size={13} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full border border-gold/50" aria-hidden="true" />
              <img
                src={IMAGES.conference}
                alt="A quiet legal conference room prepared for a professional consultation"
                className="relative h-96 w-full object-cover grayscale-[30%]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta-section" className="grain relative overflow-hidden bg-navy py-24 sm:py-36">
        <Watermark text="SMART LEGAL SOLUTIONS" mobileText="SLS" />
        <div className="relative z-10 mx-auto max-w-4xl px-5 text-center lg:px-10">
          <Reveal>
            <p className="mono-label">Professional Contact</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.12] text-ivory sm:text-5xl lg:text-6xl">
              Legal Decisions Should Begin with <span className="italic text-gold/90">Verified Facts.</span>
            </h2>
            <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-ivory/55">
              For professional contact, provide the basic procedural details of the matter without disclosing
              unnecessary confidential information through the website.
            </p>
            <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link to="/contact" data-testid="final-cta-contact-btn" className="btn-outline-gold">Contact Office</Link>
              <Link to="/practice-areas" data-testid="final-cta-practice-btn" className="btn-ghost-ivory">View Practice Areas</Link>
              <Link to="/legal-insights" data-testid="final-cta-insights-btn" className="btn-ghost-ivory">Read Legal Insights</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
